import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  ControlValueAccessor,
  FormGroup,
  FormBuilder,
  Validators,
} from "@angular/forms";
import { NgbTypeahead } from "@ng-bootstrap/ng-bootstrap";
import { forkJoin, interval, merge, Observable, Subject } from "rxjs";
import {
  debounce,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  takeUntil,
} from "rxjs/operators";
import { IProject } from "src/app/admin/projects/shared/models/IProject";
import { ProjectService } from "src/app/admin/shared/services/project.service";
import { UserFormValues } from "src/app/admin/users/shared/user-form/user-form.component";
import { IWorkOrder } from "src/app/admin/work-orders/models/IWorkOrder";
import { WorkOrderService } from "src/app/admin/work-orders/work-order.service";

@Component({
  selector: "app-time-form",
  templateUrl: "./time-form.component.html",
  styleUrls: ["./time-form.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TimeFormComponent),
      multi: true,
    },
  ],
})
export class TimeFormComponent
  implements ControlValueAccessor, OnInit, OnDestroy {
  @ViewChild("projectSearch", { static: true }) projectSearch: NgbTypeahead;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  @Input() userId: number;
  @Input() startingProjectId?: number;
  @Input() startingWorkOrderId?: number;
  manuallyChooseWorkOrder = false;

  allProjects: IProject[] = [];
  recentProjects: IProject[] = [];
  projectWorkOrders: IWorkOrder[] = [];

  form: FormGroup;
  destroy = new Subject<any>();

  get value(): UserFormValues {
    return this.form.value;
  }
  set value(value: UserFormValues) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get projectControl() {
    return this.form.controls.project;
  }
  get workOrderControl() {
    return this.form.controls.workOrder;
  }
  get workDayControl() {
    return this.form.controls.workDay;
  }
  get forThirdPartyControl() {
    return this.form.controls.forThirdParty;
  }
  get hoursControl() {
    return this.form.controls.hours;
  }
  get minutesControl() {
    return this.form.controls.minutes;
  }
  get totalMinutesControl() {
    return this.form.controls.totalMinutes;
  }
  get manuallyChooseControl() {
    return this.form.controls.manuallyChooseWorkOrder;
  }

  constructor(
    private fb: FormBuilder,
    private workOrderService: WorkOrderService,
    private projectService: ProjectService
  ) {
    this.form = this.fb.group(
      {
        project: [null, Validators.required],
        workOrder: "",
        workDay: ["", Validators.required],

        forThirdParty: [false],
        hours: [0],
        minutes: [0],
        totalMinutes: [],
        manuallyChooseWorkOrder: [],
      },
      { validators: [this.PositiveTimeValidator()] }
    );
    this.form.valueChanges.pipe(takeUntil(this.destroy)).subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });
  }
  ngOnInit() {
    this.projectControl.valueChanges
      .pipe(debounce(() => interval(250)))
      .subscribe((selectedProject: IProject) => {
        if (selectedProject) {
          this.workOrderService
            .getWorkOrdersByProject(selectedProject.id)
            .subscribe((workOrders: IWorkOrder[]) => {
              this.projectWorkOrders = workOrders;
              let selectedWorkOrder = workOrders[0];
              if (this.startingWorkOrderId) {
                selectedWorkOrder =
                  workOrders.find((workOrder) => {
                    return workOrder.id === this.startingWorkOrderId;
                  }) || selectedWorkOrder;
              }
              this.workOrderControl.setValue(selectedWorkOrder);
            });
        }
      });

    //this.loadingSubject.next(true);
    forkJoin([
      this.projectService.getProjects(),
      this.projectService.getRecentProjects(this.userId),
    ])
      //.pipe(finalize(() => this.loadingSubject.next(false)))
      .subscribe(([projects, recentProjects]) => {
        this.allProjects = projects;
        this.recentProjects = recentProjects;
        if (this.startingProjectId) {
          const selectedProject = this.allProjects.find((project) => {
            return project.id === this.startingProjectId;
          });
          this.projectControl.setValue(selectedProject);
        }
      });
  }
  ngOnDestroy(): void {
    this.destroy.next();
  }

  onChange: any = () => {};
  onTouched: any = () => {};
  writeValue(value) {
    if (value) {
      this.value = value;
    }
    if (value === null) {
      this.form.reset();
    }
  }
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    throw new Error("Method not implemented.");
  }
  validate() {
    return this.form.valid ? null : { timeEntry: { valid: false } };
  }
  search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(
      debounceTime(200),
      distinctUntilChanged()
    );
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.projectSearch.isPopupOpen())
    );
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) =>
        (term === ""
          ? this.allProjects.slice(0, 10)
          : this.allProjects.filter(
              (p) => p.name.toLowerCase().indexOf(term.toLowerCase()) > -1
            )
        ).slice(0, 10)
      )
    );
  };
  projectSearchFormatter(project: IProject) {
    return project.name;
  }
  private PositiveTimeValidator() {
    return (formGroup: FormGroup) => {
      const hoursControl = formGroup.controls.hours;
      const minutesControl = formGroup.controls.minutes;
      const totalMinutesControl = formGroup.controls.totalMinutes;

      const hours = parseInt(hoursControl.value, 10);
      const minutes = parseInt(minutesControl.value, 10);
      if (hours <= 0 && minutes <= 0) {
        totalMinutesControl.setErrors({ positiveTime: true });
      } else {
        // if we have other group errors this will remove them?
        totalMinutesControl.setErrors(null);
      }
    };
  }
}
